/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    DoipClient,
    AbstractDoipClient,
    DoipRequest,
    ServiceInfo,
    Options,
    DoipResponse,
    AuthenticationInfo,
    PasswordAuthenticationInfo,
    PrivateKeyAuthenticationInfo,
    TokenAuthenticationInfo,
    DoipConstants,
    DoipRequestUtil
} from '@cnri/doip-client';

export class TokenUsingDoipClient extends AbstractDoipClient {

    private readonly doipClient: DoipClient;

    userToServiceToTokenMap: any = {};

    constructor(doipClient: DoipClient) {
        super();
        this.doipClient = doipClient;
        if ('defaultOptions' in doipClient) this.defaultOptions = doipClient.defaultOptions as Options;
        this.userToServiceToTokenMap = this.loadAuthTokens();
    }

    close(): Promise<void> {
        return this.doipClient.close();
    }

    setCurrentUserKey(userKey: string | undefined): void {
        if (userKey) {
            this.defaultOptions = { ...this.defaultOptions, authentication: { username: userKey } };
        } else if (this.defaultOptions) {
            delete this.defaultOptions.authentication;
        }
    }

    async performOperationViaRequest(doipRequest: DoipRequest, options?: Options): Promise<DoipResponse> {
        doipRequest = this.augmentedRequest(doipRequest, options);
        let isCachedToken = false;
        let doipRequestToSend = doipRequest;
        let userKey: string | undefined;
        const serviceInfoForRequest = await this.getServiceInfoForRequest(doipRequest, options);
        if (doipRequest.authentication instanceof TokenAuthenticationInfo) {
            // don't look up a cached token, just use the one in the authenticaiton
        } else {
            userKey = this.getUserKeyFromDoipRequest(doipRequest);
            if (userKey) {
                let token = this.getTokenFromCache(userKey, serviceInfoForRequest);
                if (!token && doipRequest.authentication) {
                    const authTokenResponse = await this.acquireNewToken(doipRequest.authentication, serviceInfoForRequest);
                    token = authTokenResponse.access_token as string;
                    this.cacheToken(userKey, serviceInfoForRequest, token);
                }
                if (token) {
                    isCachedToken = true;
                    doipRequestToSend = { ...doipRequest };
                    doipRequestToSend.authentication = new TokenAuthenticationInfo(doipRequest.clientId, token);
                }
            }
        }
        if (this.isPlaceholderAuthentication(doipRequestToSend.authentication as Record<string, unknown> | undefined)) {
            delete doipRequestToSend.authentication;
        }
        let doipResponse = await this.doipClient.performOperation(doipRequestToSend, options);
        if (isCachedToken && this.isUnauthorizedResponse(doipResponse)) {
            this.removeToken(userKey, serviceInfoForRequest);
            if (DoipRequestUtil.isCompact(doipRequest.input) && doipRequest.authentication) {
                const authTokenResponse = await this.acquireNewToken(doipRequest.authentication, serviceInfoForRequest);
                const token = authTokenResponse.access_token as string;
                this.cacheToken(userKey!, serviceInfoForRequest, token);
                doipRequestToSend = { ...doipRequest };
                doipRequestToSend.authentication = new TokenAuthenticationInfo(doipRequest.clientId, token);
                doipResponse = await this.doipClient.performOperation(doipRequestToSend, options);
            }
        }
        return doipResponse;
    }

    private isPlaceholderAuthentication(auth: Record<string, unknown> | undefined): boolean {
        return !!(auth && auth.username && !auth.password);
    }

    async changePassword(newPassword: string, authInfo: AuthenticationInfo): Promise<any> {
        const request: DoipRequest = {
            targetId: "service",
            operationId: "20.DOIP/Op.ChangePassword",
            authentication: authInfo,
            input: {
                password: newPassword
            }
        };
        const response = await this.doipClient.performOperation(request);
        const responseJson = await response.asJson();
        return responseJson;
    }

    private isUnauthorizedResponse(doipResponse: DoipResponse): boolean {
        if (doipResponse.getStatus() === DoipConstants.STATUS_UNAUTHENTICATED) {
            return true;
        } else {
            return false;
        }
    }

    private removeToken(userKey: string | undefined, serviceInfo: ServiceInfo): void {
        if (!userKey) {
            return;
        }
        const serviceToTokenMap = this.userToServiceToTokenMap[userKey];
        if (!serviceToTokenMap) {
            return;
        }
        const serviceKey = this.getKeyForServiceInfo(serviceInfo);
        delete serviceToTokenMap[serviceKey];
        this.storeAuthTokens();
    }

    private cacheToken(userKey: string, serviceInfo: ServiceInfo, token: string): void {
        let serviceToTokenMap = this.userToServiceToTokenMap[userKey];
        if (!serviceToTokenMap) {
            serviceToTokenMap = {};
            this.userToServiceToTokenMap[userKey] = serviceToTokenMap;
        }
        const serviceKey = this.getKeyForServiceInfo(serviceInfo);
        serviceToTokenMap[serviceKey] = token;
        this.storeAuthTokens();
    }

    private getTokenFromCache(userKey: string, serviceInfo: ServiceInfo | undefined): string | undefined {
        //TODO consider storing tokenInfo rather than just token
        //and look at exp and last used values as in CordraClient
        if (!userKey) {
            return undefined;
        }
        const serviceToTokenMap = this.userToServiceToTokenMap[userKey];
        if (!serviceToTokenMap) {
            return undefined;
        }
        if (!serviceInfo) {
            return undefined;
        }
        const serviceKey = this.getKeyForServiceInfo(serviceInfo);
        const token = serviceToTokenMap[serviceKey];
        return token;
    }

    private storeAuthTokens(): void {
        if (localStorage) {
            localStorage.setItem('TokenUsingDoipClient.authTokens', JSON.stringify(this.userToServiceToTokenMap));
        }
    }

    private loadAuthTokens(): any {
        if (localStorage) {
            const storedTokensJson = localStorage.getItem('TokenUsingDoipClient.authTokens');
            if (storedTokensJson) {
                return JSON.parse(storedTokensJson);
            }
        }
        return {};
    }

    async authenticate(authInfo: AuthenticationInfo): Promise<any> {
        const serviceInfoForRequest: ServiceInfo | undefined = this.defaultOptions?.serviceInfo;
        if (!serviceInfoForRequest) {
            throw new Error("No serviceInfo provided for authenticate request");
        }
        const authTokenResponse = await this.acquireNewToken(authInfo, serviceInfoForRequest);
        const token: string = authTokenResponse.access_token;
        const userKey = this.getUserKey(authInfo);
        if (userKey) this.cacheToken(userKey, serviceInfoForRequest, token);
        return authTokenResponse;
    }

    private async createAuthTokenInput(authInfo: AuthenticationInfo | unknown): Promise<any> {
        let inputJson: any = {};
        if (authInfo instanceof PasswordAuthenticationInfo) {
            inputJson.grant_type = "password";
            inputJson.username = authInfo.username;
            inputJson.password = authInfo.password;
        } else if (authInfo instanceof PrivateKeyAuthenticationInfo) {
            inputJson.grant_type = "urn:ietf:params:oauth:grant-type:jwt-bearer";
            const jwt = await authInfo.createBearerToken();
            inputJson.assertion = jwt; //odd that this token will be slightly different than the one in the DOIP request headers???
        } else {
            inputJson = authInfo;
        }
        // else if (authInfo instanceof TokenAuthenticationInfo) {
        //     inputJson.grant_type = "urn:ietf:params:oauth:grant-type:jwt-bearer";
        //     inputJson.assertion = authInfo.getToken();
        // }
        return inputJson;
    }

    async signOut(): Promise<any> {
        const serviceInfoForRequest: ServiceInfo | undefined = this.defaultOptions?.serviceInfo;
        if (!serviceInfoForRequest) {
            throw new Error("No serviceInfo provided for sign out request");
        }
        const userKey = this.getUserKey(this.defaultOptions?.authentication);
        if (!userKey) {
            return { active: false };
        }
        const token = this.getTokenFromCache(userKey, serviceInfoForRequest);
        if (!token) {
            return { active: false };
        }
        const input = {
            token
        };
        const revokeTokenRequest: DoipRequest = {
            targetId: serviceInfoForRequest?.serviceId || 'service',
            operationId: "20.DOIP/Op.Auth.Revoke",
            input
        };
        const authTokenResponse = await this.doipClient.performOperation(revokeTokenRequest);
        const authTokenResponseJson = await authTokenResponse.asJson();
        this.removeToken(userKey, serviceInfoForRequest);
        return authTokenResponseJson;
    }

    private async acquireNewToken(authInfo: AuthenticationInfo | unknown, serviceInfo: ServiceInfo): Promise<any> {
        const input: any = await this.createAuthTokenInput(authInfo);
        const tokenRequest: DoipRequest = {
            targetId: serviceInfo?.serviceId || 'service',
            operationId: "20.DOIP/Op.Auth.Token",
            input
        };
        const authTokenResponse = await this.doipClient.performOperation(tokenRequest);
        const authTokenResponseJson = await authTokenResponse.asJson();
        return authTokenResponseJson;
    }

    private getKeyForServiceInfo(serviceInfo: ServiceInfo): string {
        if (serviceInfo.serviceId) {
            return serviceInfo.serviceId;
        }
        if (serviceInfo.baseUri) {
            return serviceInfo.baseUri;
        }
        throw new Error('Unable to get key for serviceInfo. No serviceId or baseUri');
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private getUserKey(authentication: AuthenticationInfo | unknown | undefined): string | undefined {
        if (!authentication) return undefined;
        if (authentication instanceof AuthenticationInfo) {
            if (authentication.getClientId()) return authentication.getClientId();
            if (authentication instanceof PasswordAuthenticationInfo) return authentication.username;
        }
        if ((authentication as { username: string }).username) {
            return (authentication as { username: string }).username;
        }
        return undefined;
    }

    private getUserKeyFromDoipRequest(doipRequest: DoipRequest): string | undefined {
        if (doipRequest.clientId) {
            return doipRequest.clientId;
        }
        return this.getUserKey(doipRequest.authentication);
    }

    getAccessToken(): string | undefined {
        const userKey = this.getUserKey(this.defaultOptions?.authentication);
        if (!userKey) {
            return undefined;
        }
        const serviceInfo: ServiceInfo | undefined = this.defaultOptions?.serviceInfo;
        const token = this.getTokenFromCache(userKey, serviceInfo);
        return token;
    }
}
